import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getAllBlocks,
  getEngineerPrioritiesByProjectAndUserId,
  getUnitsByProjectId,
  setQualityScoreByTaskID,
  updateLiveTask,
  updateTask,
  updateTaskForTomorrow,
  getLowLevelEngineersByProjectId,
  getContractorAndArchitect
} from '../../services/api/implementation/impl';
import mixpanel from 'mixpanel-browser';
import { useLocation } from 'react-router-dom';
import Loader from '../../../shared/components/ui/Loader';
import Layout from '../../components/layout/AppLayout';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import Header from '../../../shared/components/layout/Header';
import { transformData } from '../../../super-admin/utils/transformData';
import NoDataFoundImg from '../../../shared/assets/png/noDataFound.png';
import noDataIcon from '../../../shared/assets/svg/NoData.svg';
import DP from '../../../shared/assets/svg/noUser.svg';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import Modal from '../../../shared/components/ui/Modal';
import AddRequirementAll from './components/AddRequirementAll';
import AddTaskUpdate from './components/AddTaskUpdate';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import Multiselect from 'multiselect-react-dropdown';

const Tasks = () => {
  const location = useLocation();
  const { projectID, selectedDate, userID, taskType } = location.state;
  const { collapsed } = useSidebar();
  const [type, setType] = useState();
  const [selectedBlock, setSelectedBlock] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [showQualityScoreModal, setShowQualityScoreModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [qualityScore, setQualityScore] = useState(5);
  const [showDateModal, setShowDateModal] = useState(false);
  const [tempDate, setTempDate] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showKickstartModal, setShowKickstartModal] = useState(false);
  const [typeHeaderRef, setTypeHeaderRef] = useState(null);
  const [typeDropdownRect, setTypeDropdownRect] = useState(null);
  const [blockHeaderRef, setBlockHeaderRef] = useState(null);
  const [blockDropdownRect, setBlockDropdownRect] = useState(null);
  const [unitHeaderRef, setUnitHeaderRef] = useState(null);
  const [unitDropdownRect, setUnitDropdownRect] = useState(null);
  const [showPossessionDateModal, setShowPossessionDateModal] = useState(false);

  // Employees Allocation modal states
  const [showEmployeesAllocationModal, setShowEmployeesAllocationModal] = useState(false);
  const [engineers, setEngineers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [loadingEngineers, setLoadingEngineers] = useState(false);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const {
    data: priorities,
    refetch: refetchPriorities,
    isLoading: isLoadingPriorities
  } = useQuery({
    queryKey: ['tasks', projectID, selectedDate, type, selectedBlock, selectedUnit],
    queryFn: () =>
      getEngineerPrioritiesByProjectAndUserId(
        projectID,
        selectedDate,
        userID,
        taskType,
        type,
        selectedBlock,
        selectedUnit
      ),
    enabled: !!projectID && !!selectedDate,
    onSuccess: (data) => {
      mixpanel.track('Follow Ups API Success', {
        user_id: userID,
        project_id: projectID,
        follow_ups_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Follow Ups API Failed', {
        user_id: userID,
        project_id: projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const { data: AllBlocks, refetch: refetchBlocks } = useQuery({
    queryKey: ['getAllBlocks', projectID],
    queryFn: () => getAllBlocks(projectID),
    enabled: !!projectID
  });

  const { data: AllUnits, refetch: refetchUnits } = useQuery({
    queryKey: ['blocks', projectID],
    queryFn: () => getUnitsByProjectId(projectID),
    enabled: !!projectID
  });

  // Fetch engineers and contractors for employees allocation
  const fetchEngineers = async (existingEngineerId = null) => {
    if (loadingEngineers || !projectID) return;
    setLoadingEngineers(true);
    try {
      const res = await getLowLevelEngineersByProjectId(projectID);
      setEngineers(res || []);

      // Prefill selected engineer if ID is provided and found in the list
      if (existingEngineerId && res) {
        const prefilledEngineer = res.find((engineer) => engineer.id === existingEngineerId);
        if (prefilledEngineer) {
          setSelectedEngineer(prefilledEngineer);
        } else {
          // If the existing engineer is not in the low-level list, clear selection
          setSelectedEngineer(null);
        }
      } else {
        // No existing engineer to prefill, clear selection
        setSelectedEngineer(null);
      }
    } catch (error) {
      console.error('Error fetching engineers:', error);
      setEngineers([]);
    } finally {
      setLoadingEngineers(false);
    }
  };

  const fetchContractors = async () => {
    if (loadingContractors) return;
    setLoadingContractors(true);
    try {
      const res = await getContractorAndArchitect(1);
      setContractors(res?.data || []);
    } catch (error) {
      console.error('Error fetching contractors:', error);
      setContractors([]);
    } finally {
      setLoadingContractors(false);
    }
  };

  const transformedDataBlocks = (AllBlocks || [])
    ?.map((item) => ({
      label: item.name || '',
      id: item.id,
      value: item.id
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const transformedDataUnits =
    AllUnits?.filter((item) => item.accommodation?.numberOfFloors !== null)
      .map((item) => ({
        label: item.unitName || '',
        id: item.id,
        value: item.id
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  useEffect(() => {
    if (projectID && selectedDate && userID) {
      refetchPriorities();
      refetchBlocks();
      refetchUnits();
    }
  }, [projectID, selectedDate, userID]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      if (openDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  const taskFormat = [
    'name',
    'block',
    'unit',
    'status',
    'type',
    'date',
    'assigned_to',
    'actions',
    'id',
    'isReview',
    'isEscalated',
    'note'
  ];

  console.log('Priorities data:', priorities?.data);

  // PortalDropdown component for type filter
  const PortalDropdown = ({ anchorRect, children, onClose }) => {
    if (!anchorRect) return null;
    return createPortal(
      <div
        className="absolute bg-white border rounded shadow z-[1000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
        <div className="fixed inset-0 z-[-1]" onClick={onClose} />
      </div>,
      document.body
    );
  };

  // Get unique blocks from data for filter options
  const uniqueBlocks = React.useMemo(() => {
    if (!priorities?.data) return [];
    const blocks = [
      ...new Set(priorities.data.map((task) => task.liveTask?.block?.id).filter(Boolean))
    ];
    return blocks.map((blockId) => {
      const blockData = priorities.data.find((task) => task.liveTask?.block?.id === blockId)
        ?.liveTask?.block;
      return { label: blockData?.name || 'Unknown Block', value: blockId };
    });
  }, [priorities?.data]);

  // Get unique units from data for filter options
  const uniqueUnits = React.useMemo(() => {
    if (!priorities?.data) return [];
    const units = [
      ...new Set(priorities.data.map((task) => task.liveTask?.unit?.id).filter(Boolean))
    ];
    return units.map((unitId) => {
      const unitData = priorities.data.find((task) => task.liveTask?.unit?.id === unitId)?.liveTask
        ?.unit;
      return { label: unitData?.unitName || 'Unknown Unit', value: unitId };
    });
  }, [priorities?.data]);

  // Handler to open dropdown and set position
  const handleHeaderDropdownClick = (type, event) => {
    // If clicking the same dropdown that's already open, close it
    if (openDropdown === type) {
      setOpenDropdown(null);
      return;
    }

    if (type === 'type' && typeHeaderRef) {
      setTypeDropdownRect(typeHeaderRef.getBoundingClientRect());
    }
    if (type === 'block' && blockHeaderRef) {
      setBlockDropdownRect(blockHeaderRef.getBoundingClientRect());
    }
    if (type === 'unit' && unitHeaderRef) {
      setUnitDropdownRect(unitHeaderRef.getBoundingClientRect());
    }
    setOpenDropdown(type);
  };

  // Handler to close dropdown
  const handleCloseDropdown = () => setOpenDropdown(null);

  // Handler to select type filter
  const handleSelectTypeFilter = (typeValue) => {
    setType(typeValue);
    setOpenDropdown(null);
  };

  // Handler to select block filter
  const handleSelectBlockFilter = (blockValue) => {
    setSelectedBlock(blockValue);
    setOpenDropdown(null);
  };

  // Handler to select unit filter
  const handleSelectUnitFilter = (unitValue) => {
    setSelectedUnit(unitValue);
    setOpenDropdown(null);
  };

  const tasksData = priorities?.data
    ?.filter((el) => el?.status !== 'Completed') // Exclude completed tasks
    ?.map((el) => {
      return transformData(
        {
          name: el?.liveTask?.name || '---',
          block: el?.liveTask?.block?.name || '---',
          unit: el?.liveTask?.unit?.unitName || '---',
          status: el?.status || '---',
          type: el?.type || '---',
          date: formatDate(el?.date) || '---',
          assigned_to: el?.user?.name || '---',
          actions: `Actions---${el.id}---${el?.task_id || ''}`,
          id: el?.id,
          isReview: el?.isReview,
          isEscalated: el?.isEscalated,
          note: el?.notes || '---'
        },
        taskFormat
      );
    });

  function formatDate(dateString) {
    if (!dateString) return '---';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '---';
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  }

  const handleQualityScoreSubmit = async () => {
    if (!selectedTask) return;

    try {
      setIsSubmitting(true);
      const payload = {
        status: 'Completed',
        qualityScore: qualityScore,
        dailyTask_id: selectedTask.id
      };

      console.log('Quality Score Payload:', payload);
      const res = await setQualityScoreByTaskID(selectedTask.task_id, payload);

      toast.success('Quality score submitted successfully!');
      setShowQualityScoreModal(false);
      setSelectedTask(null);
      setQualityScore(5); // Reset to default
      await refetchPriorities(); // Refresh the tasks list
    } catch (error) {
      console.error('Error submitting quality score:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to submit quality score'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateSubmit = async () => {
    if (!tempDate || !selectedTask) return;

    try {
      setIsSubmitting(true);

      const payload = {
        dailyTask_id: selectedTask.id,
        planned_start_date: tempDate
      };

      console.log('Date Update Payload:', payload);
      // You can call your API here to update the task with the new date
      // For example: await updateTaskDate(selectedTask.id, payload);
      updateLiveTask(selectedTask.task_id, payload);
      toast.success('Date updated successfully!');
      setShowDateModal(false);
      setSelectedTask(null);
      setTempDate(null);
      await refetchPriorities(); // Refresh the tasks list
      window.location.reload();
    } catch (error) {
      console.error('Error updating date:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update date');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePossessionDateSubmit = async () => {
    if (!tempDate || !selectedTask) return;

    try {
      setIsSubmitting(true);

      const payload = {
        dailyTask_id: selectedTask.id,
        planned_start_date: tempDate
      };

      console.log('Possession Date Update Payload:', payload);
      // You can call your API here to update the task with the new date
      // For example: await updateTaskDate(selectedTask.id, payload);
      updateLiveTask(selectedTask.task_id, payload);
      toast.success('Possession Date updated successfully!');
      setShowPossessionDateModal(false);
      setSelectedTask(null);
      setTempDate(null);
      await refetchPriorities(); // Refresh the tasks list
      window.location.reload();
    } catch (error) {
      console.error('Error updating possession date:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to update possession date'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Kickstart handlers
  const handleKickstartUpdate = async () => {
    try {
      setIsSubmitting(true);
      const payload = {
        status: 'In Progress',
        dailyTask_id: selectedTask?.id
      };
      await updateTask(selectedTask?.task_id, payload);
      toast.success('Task started today successfully!');
      setShowKickstartModal(false);
      setSelectedTask(null);
      await refetchPriorities(); // Refresh the tasks list
    } catch (error) {
      console.error('Error updating Kickstart task:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to start task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKickstartUpdateForTomorrow = async () => {
    try {
      setIsSubmitting(true);
      const payload = {
        status: 'Completed'
      };
      await updateTaskForTomorrow(selectedTask?.id, payload);
      toast.success('Task scheduled for tomorrow successfully!');
      setShowKickstartModal(false);
      setSelectedTask(null);
      await refetchPriorities(); // Refresh the tasks list
    } catch (error) {
      console.error('Error updating Kickstart task:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to schedule task');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Employees Allocation handlers
  const handleEmployeesAllocationSubmit = async () => {
    if (!selectedTask) return;

    // Validate both fields are mandatory
    if (!selectedEngineer) {
      toast.error('Please select an engineer');
      return;
    }

    if (selectedContractors.length === 0) {
      toast.error('Please select at least one contractor');
      return;
    }

    try {
      setIsSubmitting(true);

      // Collect all engineer IDs (existing + newly selected)
      const allEngineerIds = [
        selectedEngineer.id,
        ...(selectedTask?.liveTask?.engineerLiveTaskMapping?.map((e) => e.user?.id) || [])
      ];

      // Remove duplicates from engineer IDs
      const uniqueEngineerIds = [...new Set(allEngineerIds.filter((id) => id))];

      // Remove duplicates from contractor IDs
      const uniqueContractorIds = [
        ...new Set(selectedContractors?.map((c) => c.id).filter((id) => id))
      ];

      const payload = {
        engineer_ids: uniqueEngineerIds,
        sp_ids: uniqueContractorIds,
        dailyTask_id: selectedTask.id
      };

      console.log('Employees Allocation Payload:', payload);

      const res = await updateLiveTask(selectedTask.task_id, payload);

      toast.success('Employees allocated successfully!');
      setShowEmployeesAllocationModal(false);
      setSelectedTask(null);
      setSelectedEngineer(null);
      setSelectedContractors([]);
      await refetchPriorities(); // Refresh the tasks list
    } catch (error) {
      console.error('Error allocating employees:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to allocate employees'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const filterTypeOptions = [
    {
      value: '',
      label: type ? 'Clear Filter' : 'All Types'
    },
    {
      value: 'default',
      label: 'Update'
    },
    {
      value: 'Date',
      label: 'Date'
    },
    {
      value: 'Review',
      label: 'Review'
    },
    {
      value: 'Escalated',
      label: 'Escalated'
    },
    {
      value: 'Kickstart',
      label: 'Kickstart'
    },
    ...(taskType !== 'Possession Date'
      ? [
          {
            value: 'Posession Date',
            label: 'Possession Date'
          }
        ]
      : []),
    ...(taskType !== 'Employees Allocation'
      ? [
          {
            value: 'Employees Allocation',
            label: 'Employees Allocation'
          }
        ]
      : [])
  ];

  return (
    <Layout>
      <Loader isVisible={isLoadingPriorities || isSubmitting} />

      {showQualityScoreModal && (
        <Modal
          isDirty
          confirmOnClose
          width={45}
          name="Quality Score"
          onClose={() => setShowQualityScoreModal(false)}>
          <div className="flex flex-col justify-start items-center z-10">
            <div className="w-full mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {selectedTask?.name || 'Task Name'}
              </h3>
            </div>

            {/* Quality Score Selection */}
            <div className="w-full mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Select Quality Score:</p>
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                <div className="flex flex-wrap gap-2 justify-center">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                    <button
                      key={score}
                      onClick={() => setQualityScore(score)}
                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center font-semibold text-base transition-colors ${
                        qualityScore === score
                          ? 'bg-[#346C75] border-[#346C75] text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-[#346C75]'
                      }`}>
                      {score}
                    </button>
                  ))}
                </div>
                <p className="text-center text-gray-500 text-sm mt-3">
                  Selected Score: {qualityScore}/10
                </p>
              </div>
            </div>

            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="reset"
                onClick={() => setShowQualityScoreModal(false)}
                displayType="secondary">
                Cancel
              </Button>

              <Button disabled={isSubmitting} type="button" onClick={handleQualityScoreSubmit}>
                Submit Score
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showUpdateModal && (
        <Modal
          isDirty
          confirmOnClose
          width={60}
          name="Task Update"
          onClose={() => setShowUpdateModal(false)}>
          <AddTaskUpdate
            selectedTask={selectedTask}
            onSuccess={() => {
              setShowUpdateModal(false);
              setSelectedTask(null);
              refetchPriorities();
            }}
            projectId={projectID}
          />
        </Modal>
      )}

      {showRequirementModal && (
        <AddRequirementAll
          selectedTask={selectedTask}
          projectId={projectID}
          onSuccess={() => {
            setShowRequirementModal(false);
            refetchPriorities();
          }}
          isOpen={showRequirementModal}
          onClose={() => setShowRequirementModal(false)}
        />
      )}

      {showDateModal && (
        <Modal
          isDirty
          confirmOnClose
          width={25}
          name="Set Planned Start Date"
          onClose={() => setShowDateModal(false)}>
          <div className="flex flex-col justify-start items-center z-10">
            <div className="w-full mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {selectedTask?.liveTask?.name || 'Task Name'}
              </h3>
            </div>

            {/* Date Selection */}
            <div className="w-full mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Select Planned Start Date:</p>
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 flex justify-center items-center">
                <DatePicker
                  selected={tempDate}
                  onChange={(date) => setTempDate(date)}
                  inline
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>

            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="reset"
                onClick={() => {
                  setShowDateModal(false);
                  setSelectedTask(null);
                  setTempDate(null);
                }}
                displayType="secondary">
                Cancel
              </Button>

              <Button disabled={isSubmitting || !tempDate} type="button" onClick={handleDateSubmit}>
                Update Date
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showPossessionDateModal && (
        <Modal
          isDirty
          confirmOnClose
          width={25}
          name="Set Possession Date"
          onClose={() => setShowPossessionDateModal(false)}>
          <div className="flex flex-col justify-start items-center z-10">
            <div className="w-full mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {selectedTask?.liveTask?.name || 'Task Name'}
              </h3>
            </div>

            {/* Date Selection */}
            <div className="w-full mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Select Possession Date:</p>
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 flex justify-center items-center">
                <DatePicker
                  selected={tempDate}
                  onChange={(date) => setTempDate(date)}
                  inline
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>

            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="reset"
                onClick={() => {
                  setShowPossessionDateModal(false);
                  setSelectedTask(null);
                  setTempDate(null);
                }}
                displayType="secondary">
                Cancel
              </Button>

              <Button
                disabled={isSubmitting || !tempDate}
                type="button"
                onClick={handlePossessionDateSubmit}>
                Update Date
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showKickstartModal && (
        <Modal
          isDirty
          confirmOnClose
          width={40}
          name="Kickstart Task"
          onClose={() => setShowKickstartModal(false)}>
          <div className="flex flex-col justify-start items-center z-10">
            <div className="w-full mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {selectedTask?.liveTask?.name || selectedTask?.name || 'Task Name'}
              </h3>
              <p className="text-sm text-gray-600">
                This task is ready to start. When would you like to begin?
              </p>
            </div>

            <div className="w-full flex justify-center items-center space-x-4 mt-8">
              <Button
                type="button"
                onClick={() => setShowKickstartModal(false)}
                displayType="secondary">
                Cancel
              </Button>

              <Button
                type="button"
                onClick={handleKickstartUpdateForTomorrow}
                disabled={isSubmitting}
                displayType="secondary">
                Start Tomorrow
              </Button>

              <Button
                type="button"
                onClick={handleKickstartUpdate}
                disabled={isSubmitting}
                displayType="primary">
                Start Today
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showEmployeesAllocationModal && (
        <Modal
          isDirty
          confirmOnClose
          width={60}
          name="Employees Allocation"
          onClose={() => {
            setShowEmployeesAllocationModal(false);
            setSelectedEngineer(null);
            setSelectedContractors([]);
          }}>
          <div className="flex flex-col justify-start items-start z-10">
            <div className="w-full mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {selectedTask?.liveTask?.name || selectedTask?.name || 'Task Name'}
              </h3>
              <p className="text-sm text-gray-600">
                Allocate engineers and contractors for this task.
              </p>
            </div>

            {/* Engineers Selection */}
            <div className="w-full mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Engineer (Low Level) *
              </label>
              {loadingEngineers ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#346C75]"></div>
                </div>
              ) : (
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#346C75] focus:border-transparent"
                  value={selectedEngineer?.id || ''}
                  onChange={(e) => {
                    const engineerId = e.target.value;
                    const engineer = engineers.find((eng) => eng.id === parseInt(engineerId));
                    setSelectedEngineer(engineer || null);
                  }}>
                  <option value="" disabled>
                    Select Engineer
                  </option>
                  {engineers.map((engineer) => (
                    <option key={engineer.id} value={engineer.id}>
                      {engineer.name || `${engineer.first_name} ${engineer.last_name}`}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Contractors Selection */}
            <div className="w-full mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Contractors *
              </label>
              {loadingContractors ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#346C75]"></div>
                </div>
              ) : (
                <Multiselect
                  options={contractors.map((contractor) => ({
                    id: contractor.user_id,
                    name:
                      contractor.user?.name || contractor.user?.company_name || 'Unnamed Contractor'
                  }))}
                  selectedValues={selectedContractors}
                  onSelect={(selectedList) => {
                    setSelectedContractors(selectedList);
                  }}
                  onRemove={(selectedList) => {
                    setSelectedContractors(selectedList);
                  }}
                  displayValue="name"
                  placeholder="Select contractors"
                  emptyRecordMsg="No contractors available"
                  showCheckbox
                  style={{
                    optionContainer: { border: '1px solid #d1d5db', backgroundColor: 'white' },
                    option: { color: 'black', backgroundColor: 'white' },
                    chips: { background: '#346C75', color: 'white' }
                  }}
                />
              )}
            </div>

            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="button"
                onClick={() => {
                  setShowEmployeesAllocationModal(false);
                  setSelectedEngineer(null);
                  setSelectedContractors([]);
                }}
                displayType="secondary">
                Cancel
              </Button>

              <Button
                type="button"
                onClick={handleEmployeesAllocationSubmit}
                disabled={isSubmitting || !selectedEngineer || selectedContractors.length === 0}>
                Submit
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <div
        className={`fixed top-0 z-10 transition-all duration-300 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          backArrow={true}
          data={{
            name:
              taskType === 'Escalated'
                ? 'Escalated Tasks'
                : taskType === 'Review'
                  ? 'Review Tasks'
                  : taskType === 'Kickstart'
                    ? 'Kickstart Tasks'
                    : taskType === 'Possession Date'
                      ? 'Possession Date Tasks'
                      : taskType === 'Employees Allocation'
                        ? 'Employees Allocation Tasks'
                        : 'Task Updates'
          }}
        />
      </div>
      <div className="w-full flex flex-col absolute z-5 bg-gray-100 pb-10 overflow-hidden">
        <GridWrapper width={100}>
          <div className="mt-[5rem] relative mb-[3rem] bg-white rounded-lg">
            <div className="overflow-scroll hide-scrollbar relative rounded-t-lg pb-2">
              <table className="text-left w-full">
                <thead
                  className="bg-gray-200 !text-gray-600 w-full"
                  style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
                  <tr className="font-normal h-[3rem] w-full">
                    <th className="px-4 py-2 border-b font-normal">Task Name</th>
                    <th
                      className="px-4 py-2 border-b font-normal relative"
                      ref={(ref) => setBlockHeaderRef(ref)}>
                      <span>Block</span>
                      <button
                        className="ml-1 align-middle"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('block', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'block' && blockDropdownRect && (
                        <PortalDropdown
                          anchorRect={blockDropdownRect}
                          onClose={handleCloseDropdown}>
                          {[
                            {
                              value: '',
                              label: selectedBlock ? 'Clear Filter' : 'All Blocks'
                            },
                            ...transformedDataBlocks
                          ].map((blockOption, idx) => (
                            <div
                              key={blockOption.value || idx}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                blockOption.value === '' && 'bg-gray-100'
                              } ${blockOption.value === selectedBlock && 'bg-blue-100'}`}
                              onClick={() => handleSelectBlockFilter(blockOption.value)}>
                              {blockOption.label}
                            </div>
                          ))}
                        </PortalDropdown>
                      )}
                    </th>
                    <th
                      className="px-4 py-2 border-b font-normal relative"
                      ref={(ref) => setUnitHeaderRef(ref)}>
                      <span>Unit</span>
                      <button
                        className="ml-1 align-middle"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('unit', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'unit' && unitDropdownRect && (
                        <PortalDropdown anchorRect={unitDropdownRect} onClose={handleCloseDropdown}>
                          {[
                            {
                              value: '',
                              label: selectedUnit ? 'Clear Filter' : 'All Units'
                            },
                            ...transformedDataUnits
                          ].map((unitOption, idx) => (
                            <div
                              key={unitOption.value || idx}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                unitOption.value === '' && 'bg-gray-100'
                              } ${unitOption.value === selectedUnit && 'bg-blue-100'}`}
                              onClick={() => handleSelectUnitFilter(unitOption.value)}>
                              {unitOption.label}
                            </div>
                          ))}
                        </PortalDropdown>
                      )}
                    </th>
                    <th className="px-4 py-2 border-b font-normal">Status</th>

                    <th
                      className="px-4 py-2 border-b font-normal relative"
                      ref={(ref) => setTypeHeaderRef(ref)}>
                      <span>Type</span>
                      <button
                        className="ml-1 align-middle"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('type', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'type' && typeDropdownRect && (
                        <PortalDropdown anchorRect={typeDropdownRect} onClose={handleCloseDropdown}>
                          {filterTypeOptions.map((typeOption, idx) => (
                            <div
                              key={typeOption.value || idx}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                typeOption.value === '' && 'bg-gray-100'
                              } ${typeOption.value === type && 'bg-blue-100'}`}
                              onClick={() => handleSelectTypeFilter(typeOption.value)}>
                              {typeOption.label}
                            </div>
                          ))}
                        </PortalDropdown>
                      )}
                    </th>

                    <th className="px-4 py-2 border-b font-normal">Due Date</th>
                    <th className="px-4 py-2 border-b relative font-normal">Assigned To</th>
                    <th className="px-4 py-2 border-b relative font-normal">Notes</th>
                    <th className="px-4 py-2 border-b font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tasksData && tasksData?.length > 0 ? (
                    tasksData?.map((task) => (
                      <tr key={task.id} className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                        <td className="px-4 py-2">{task.name}</td>
                        <td className="px-4 py-2">{task.block}</td>
                        <td className="px-4 py-2">{task.unit}</td>
                        <td className="px-4 py-2">
                          <div className="relative z-0">
                            <span className="flex pl-6 overflow-hidden text-sm">{task.status}</span>
                            <div
                              className={`w-[5px] h-[100%] rounded-t-2xl rounded-r-[3rem] absolute left-[4px] top-[-2px] ${
                                task.status === 'Delayed'
                                  ? 'bg-[#C2281B]'
                                  : task.status === 'Completed'
                                    ? 'bg-[#3F8D78]'
                                    : task.status === 'In Progress'
                                      ? 'bg-[#3B82F6]'
                                      : 'bg-gray-400'
                              }`}>
                              &nbsp;
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-2">
                          {task?.isReview
                            ? 'Review'
                            : task?.isEscalated
                              ? 'Escalated'
                              : task?.type === 'Date'
                                ? 'Date'
                                : task?.type === 'default'
                                  ? 'Update'
                                  : task?.type}
                        </td>

                        <td className="px-4 py-2">{task.date}</td>
                        <td className="px-4 py-2">{task.assigned_to}</td>
                        <td className="px-4 py-2">{task.note}</td>

                        <td className="px-4 py-2">
                          <Button
                            onClick={() => {
                              const originalTask = priorities?.data?.find((t) => t.id === task.id);
                              setSelectedTask(originalTask);
                              if (originalTask?.type === 'Kickstart') {
                                setShowKickstartModal(true);
                              } else if (originalTask?.isReview) {
                                setShowQualityScoreModal(true);
                              } else if (originalTask?.isUpdate) {
                                setShowUpdateModal(true);
                              } else if (originalTask?.type === 'Date') {
                                setShowDateModal(true);
                              } else if (originalTask?.type === 'Posession Date') {
                                setShowPossessionDateModal(true);
                              } else if (originalTask?.type === 'Employees Allocation') {
                                // Prefill already selected contractors
                                const existingContractors =
                                  originalTask?.liveTask?.spLiveTaskMapping?.map((s) => ({
                                    id: s.user?.id,
                                    name:
                                      s.user?.name || s.user?.company_name || 'Unnamed Contractor'
                                  })) || [];
                                setSelectedContractors(existingContractors);

                                // Check for existing low-level engineer
                                const existingLowLevelEngineer =
                                  originalTask?.liveTask?.engineerLiveTaskMapping?.find(
                                    (mapping) => mapping.user?.level === 'Low'
                                  );

                                setShowEmployeesAllocationModal(true);
                                fetchEngineers(existingLowLevelEngineer?.user?.id);
                                fetchContractors();
                              } else {
                                setShowRequirementModal(true);
                              }
                            }}>
                            Update
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={taskType === 'Updates' ? 8 : 7} className="text-center py-7">
                        <div className="flex flex-col items-center justify-center">
                          <img
                            src={NoDataFoundImg}
                            alt="No Data"
                            className="w-[25rem] h-[20rem] object-cover"
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </GridWrapper>
      </div>
    </Layout>
  );
};

export default Tasks;
